import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { stageService } from '../../services/api/stageService';
import { candidateService } from '../../services/api/candidateService';
import { Stage, StageCreateDto, StageUpdateDto } from '../../types/types';

interface StagesState {
    stages: Stage[];
    loading: boolean;
    error: string | null;
    lastFetched: number | null;
    newCandidateCount: number;
    candidateCounts: Record<string, number>;
}

const initialState: StagesState = {
    stages: [],
    loading: false,
    error: null,
    lastFetched: null,
    newCandidateCount: 0,
    candidateCounts: {}
};

// Async thunks
export const fetchAllStages = createAsyncThunk(
    'stages/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const stages = await stageService.getAllStages();
            return stages;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch stages');
        }
    }
);

export const fetchActiveStages = createAsyncThunk(
    'stages/fetchActive',
    async (_, { rejectWithValue }) => {
        try {
            const stages = await stageService.getActiveStages();
            return stages;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch active stages');
        }
    }
);

export const fetchStagesWithCounts = createAsyncThunk(
    'stages/fetchWithCounts',
    async (_, { rejectWithValue }) => {
        try {
            const [stages, newCandidates, candidateCounts] = await Promise.all([
                stageService.getStagesWithCounts(),
                candidateService.getRecentCandidates(),
                stageService.getCandidateCountsByStage()
            ]);

            return {
                stages,
                newCandidateCount: newCandidates.length,
                candidateCounts
            };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch stages with counts');
        }
    }
);

export const createStage = createAsyncThunk(
    'stages/create',
    async (stageData: StageCreateDto, { rejectWithValue }) => {
        try {
            const newStage = await stageService.createStage(stageData);
            return newStage;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to create stage');
        }
    }
);

export const updateStage = createAsyncThunk(
    'stages/update',
    async ({ id, stageData }: { id: string; stageData: StageUpdateDto }, { rejectWithValue }) => {
        try {
            const updatedStage = await stageService.updateStage(id, stageData);
            return updatedStage;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update stage');
        }
    }
);

export const deleteStage = createAsyncThunk(
    'stages/delete',
    async (stageId: string, { rejectWithValue }) => {
        try {
            await stageService.deleteStage(stageId);
            return stageId;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete stage');
        }
    }
);

export const refreshCandidateCounts = createAsyncThunk(
    'stages/refreshCounts',
    async (_, { rejectWithValue }) => {
        try {
            const [newCandidates, candidateCounts] = await Promise.all([
                candidateService.getRecentCandidates(),
                stageService.getCandidateCountsByStage()
            ]);

            return {
                newCandidateCount: newCandidates.length,
                candidateCounts
            };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to refresh candidate counts');
        }
    }
);

const stagesSlice = createSlice({
    name: 'stages',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        updateCandidateCount: (state, action: PayloadAction<{ stageId: string; count: number }>) => {
            const { stageId, count } = action.payload;
            state.candidateCounts[stageId] = count;

            // Update the stage's candidate count in the stages array
            const stage = state.stages.find(s => s.id === stageId);
            if (stage) {
                stage.candidateCount = count;
            }
        },
        decrementCandidateCount: (state, action: PayloadAction<string>) => {
            const stageId = action.payload;
            if (state.candidateCounts[stageId] > 0) {
                state.candidateCounts[stageId]--;
            }

            const stage = state.stages.find(s => s.id === stageId);
            if (stage && stage.candidateCount && stage.candidateCount > 0) {
                stage.candidateCount--;
            }
        },
        incrementCandidateCount: (state, action: PayloadAction<string>) => {
            const stageId = action.payload;
            state.candidateCounts[stageId] = (state.candidateCounts[stageId] || 0) + 1;

            const stage = state.stages.find(s => s.id === stageId);
            if (stage) {
                stage.candidateCount = (stage.candidateCount || 0) + 1;
            }
        },
        handleCandidateStageChange: (state, action: PayloadAction<{ fromStageId: string; toStageId: string }>) => {
            const { fromStageId, toStageId } = action.payload;

            // Decrement from old stage
            if (state.candidateCounts[fromStageId] > 0) {
                state.candidateCounts[fromStageId]--;
            }
            const fromStage = state.stages.find(s => s.id === fromStageId);
            if (fromStage && fromStage.candidateCount && fromStage.candidateCount > 0) {
                fromStage.candidateCount--;
            }

            // Increment to new stage
            state.candidateCounts[toStageId] = (state.candidateCounts[toStageId] || 0) + 1;
            const toStage = state.stages.find(s => s.id === toStageId);
            if (toStage) {
                toStage.candidateCount = (toStage.candidateCount || 0) + 1;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all stages
            .addCase(fetchAllStages.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllStages.fulfilled, (state, action) => {
                state.loading = false;
                state.stages = action.payload;
                state.lastFetched = Date.now();
            })
            .addCase(fetchAllStages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch active stages
            .addCase(fetchActiveStages.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchActiveStages.fulfilled, (state, action) => {
                state.loading = false;
                state.stages = action.payload;
                state.lastFetched = Date.now();
            })
            .addCase(fetchActiveStages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch stages with counts
            .addCase(fetchStagesWithCounts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStagesWithCounts.fulfilled, (state, action) => {
                state.loading = false;
                state.stages = action.payload.stages;
                state.newCandidateCount = action.payload.newCandidateCount;
                state.candidateCounts = action.payload.candidateCounts;
                state.lastFetched = Date.now();
            })
            .addCase(fetchStagesWithCounts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Create stage
            .addCase(createStage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createStage.fulfilled, (state, action) => {
                state.loading = false;
                state.stages.push(action.payload);
            })
            .addCase(createStage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Update stage
            .addCase(updateStage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateStage.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.stages.findIndex(s => s.id === action.payload.id);
                if (index !== -1) {
                    state.stages[index] = action.payload;
                }
            })
            .addCase(updateStage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Delete stage
            .addCase(deleteStage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteStage.fulfilled, (state, action) => {
                state.loading = false;
                state.stages = state.stages.filter(s => s.id !== action.payload);
                delete state.candidateCounts[action.payload];
            })
            .addCase(deleteStage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Refresh candidate counts
            .addCase(refreshCandidateCounts.fulfilled, (state, action) => {
                state.newCandidateCount = action.payload.newCandidateCount;
                state.candidateCounts = action.payload.candidateCounts;

                // Update candidate counts in stages array
                state.stages.forEach(stage => {
                    stage.candidateCount = action.payload.candidateCounts[stage.id] || 0;
                });
            });
    }
});

export const {
    clearError,
    updateCandidateCount,
    decrementCandidateCount,
    incrementCandidateCount,
    handleCandidateStageChange
} = stagesSlice.actions;

export default stagesSlice.reducer;